import "core-js/modules/es.string.replace";
import "core-js/modules/es.string.replace";
/**
 * Component: Quick view
 * ------------------------------------------------------------------------------
 * A quick view component.
 *
 * @namespace quickView
 */

import axios from 'axios';
import cssClasses from '~/helpers/cssClasses';
import loaders from '~/helpers/loaders';
import { on, serialize } from '~/helpers/utils';
import Modal from '~/components/modal';
import ProductForm from '~/components/product-form';
/**
 * DOM selectors.
 */

const selectors = {
  addToCart: '[js-quick-view="addToCart"]',
  container: '[js-quick-view="container"]',
  viewport: '[js-quick-view="viewport"]',
  body: '[js-quick-view="body"]'
};
/**
 * Attribute and data selectors.
 */

const dataSelectors = {
  quickView: 'js-quick-view'
};
/**
 * Create a new quick view object.
 */

export default (() => {
  const namespace = 'quick-view';
  /**
   * DOM node selectors.
   */

  const nodeSelectors = {
    body: document.querySelector(selectors.body),
    container: document.querySelector(selectors.container),
    toggleSelectors: document.querySelector(selectors.toggleSelectors),
    viewport: document.querySelector(selectors.viewport)
  };
  /**
   * Global instances.
   */

  const modal = Modal({
    id: namespace
  });
  /**
   * Initialise component.
   */

  function init() {
    if (!nodeSelectors.container) {
      return;
    }

    setEventListeners();
    setEventBusListeners();
    modal.init();
  }
  /**
   * Set event listeners.
   */


  function setEventListeners() {
    on('click', nodeSelectors.container, event => handleQuickViewClick(event));
  }
  /**
   * Set EventBus listeners on component.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('QuickView:open', response => handleQuickViewEvent(response));
  }
  /**
   * Handle quick view toggle click event.
   * @param {Event} event - Click event.
   */


  function handleQuickViewClick(event) {
    if (!isTargetToggle(event.target)) {
      return;
    }

    event.preventDefault();
    const productHandle = event.target.dataset.productUrl.replace('/products/', '');
    Frame.EventBus.emit('QuickView:open', productHandle);
  }
  /**
   * Handle quick view eventbus event.
   * @param {String} handle - The product handle to quick view.
   */


  function handleQuickViewEvent(handle) {
    setLoadingState();
    toggleQuickView(handle);
  }
  /**
   * Detect quick view toggle click.
   * @param {HTMLElement} target - Clicked target to check quick view attributes against.
   * @returns {Boolean}
   */


  function isTargetToggle(target) {
    return typeof target.attributes[dataSelectors.quickView] !== 'undefined' && target.attributes[dataSelectors.quickView].nodeValue === 'toggle';
  }
  /**
   * Toggle quick view modal after fetching data from toggle target.
   * @param {String} handle - The product handle to quick view.
   */


  function toggleQuickView(handle) {
    modal.show(namespace);
    axios.get(getProductUrl(handle)).then(response => {
      nodeSelectors.body.innerHTML = response.data;
      removeLoadingState();
      initProductForm();
      setAddToCartEvents();
      return response;
    }).catch(error => {
      throw new Error(error);
    });
  }
  /**
   * Set loading state.
   */


  function setLoadingState() {
    nodeSelectors.viewport.classList.add(cssClasses.loading);
    nodeSelectors.body.innerHTML = loaders.ballPulse;
  }
  /**
   * Remove loading state.
   */


  function removeLoadingState() {
    nodeSelectors.viewport.classList.remove(cssClasses.loading);
  }
  /**
   * Initialise product form.
   */


  function initProductForm() {
    ProductForm({
      container: 'quick-view',
      enableSwatches: true
    }).init();

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }
  /**
   * Set click events on buttons to post form data.
   */


  function setAddToCartEvents() {
    on('click', document.querySelector(selectors.addToCart), event => handleAddToCart(event));
  }
  /**
   * Handle add to cart event and set loading state.
   * @param {Object} event - Click and key down event.
   */


  function handleAddToCart(event) {
    event.preventDefault();
    const target = event.currentTarget;
    setAddingToCartState(target);
    axios.post('/cart/add.js', serialize(target.closest('form'))).then(response => {
      setSuccessState(target, response);
      return response;
    }).catch(error => {
      throw new Error(error);
    });
  }
  /**
   * Set success state when item has been successfully added.
   * @param {HTMLElement} target - Add to cart button.
   * @param {Object} response - Successful JSON response from add.js.
   */


  function setSuccessState(target, response) {
    setDefaultState(target);
    window.setTimeout(() => {
      Frame.EventBus.emit('AjaxCart:itemAdded', response);
    }, 500);
    modal.close(namespace);
  }
  /**
   * Reset the loading states back to default.
   * @param {Object} target clicked add to cart button in DOM.
   */


  function setDefaultState(target) {
    target.innerText = theme.strings.addToCart;
  }
  /**
   * Set adding state to add to cart button.
   * @param {Object} target clicked add to cart button in DOM.
   */


  function setAddingToCartState(target) {
    target.innerText = theme.strings.addingToCart;
  }
  /**
   * Get quick view product template url.
   * @param {String} handle - The product handle to quick view.
   * @returns {String}
   */


  function getProductUrl(handle) {
    return "/products/".concat(handle, "?view=quick-view");
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init
  });
});